import { ursaColors, lodestarColors } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { themed } from "@smartsheet/sds-theme-internal";
import { BaseButtonStyledProps, ButtonRenderState } from "../types";
import { mapButtonAppearance } from "./mapButtonAppearance";

// In app-core (oldestar theme), Buttons have an opacity of 0.3 applied to them when they are
// disabled. We've used gpick to approximate this for the oldestar theme w/o an opacity filter.
export const buttonBackgroundColors = themed({
    primary: {
        //only used in lodestar-light
        rest: lodestarColors.buttonPrimary, //#3576ba no token exists
        focus: lodestarColors.buttonPrimaryFocus, //#3576ba no token exists
        hover: lodestarColors.buttonPrimaryHover, //#005D95 no token exists
        active: lodestarColors.buttonPrimaryPressed, //#004173 no token exists
        disabled: lodestarColors.buttonPrimaryDisabled, //#b3ccea no token exists
    },
    secondary: {
        //only used in lodestar-light
        rest: lodestarColors.buttonDefault, //#eeeeee no token exists
        focus: lodestarColors.buttonDefaultFocus, //#eeeeee no token exists
        hover: color("color--background--neutral--default", lodestarColors.buttonDefaultHover),
        active: color("color--background--neutral--strong", lodestarColors.buttonDefaultPressed),
        disabled: color("color--background--neutral--weaker", lodestarColors.buttonDefaultDisabled),
    },
    destructive: {
        //only used in lodestar-light
        rest: lodestarColors.buttonDestructive, //#cc2e3b no token exists
        focus: lodestarColors.buttonDestructiveFocus, //#cc2e3b no token exists
        hover: color("color--background--destructive--weak", lodestarColors.dangerLight10),
        active: color("color--background--destructive--strong", lodestarColors.buttonDestructivePressed),
        disabled: lodestarColors.buttonDestructiveDisabled, //#edbec2 no token exists
    },
    "secondary-destructive": {
        //only used in lodestar-light
        rest: lodestarColors.buttonDefault, //#eeeeee no token exists
        focus: lodestarColors.buttonDefaultFocus, //#eeeeee no token exists
        hover: color("color--background--neutral--default", lodestarColors.buttonDefaultHover),
        active: color("color--background--neutral--strong", lodestarColors.buttonDefaultPressed),
        disabled: color("color--background--neutral--weaker", lodestarColors.buttonDefaultDisabled),
    },
    share: {
        //only used in lodestar-light
        rest: lodestarColors.ctaShare, //#007e84 no token exists
        focus: lodestarColors.ctaShareFocus, //#007e84 no token exists
        hover: lodestarColors.ctaShareHover, //#40bac0 no token exists
        active: lodestarColors.ctaShareActive, //#01595d no token exists
        disabled: lodestarColors.ctaShareDisabled, //#aed5d6 no token exists
    },
    borderless: {
        //only used in lodestar-light
        rest: "transparent",
        hover: lodestarColors.buttonDefault, //#eeeeee no token exists
        active: color("color--background--neutral--default", lodestarColors.buttonDefaultHover),
        focus: lodestarColors.buttonDefault, //#eeeeee no token exists
        disabled: "transparent",
    },
    link: {
        rest: "transparent",
        hover: "transparent",
        active: "transparent",
        focus: "transparent",
        disabled: "transparent",
    },
    //Below are exclusive to ursa-light
    "primary-filled": {
        rest: color("color--background--primary--default", ursaColors.blue500),
        hover: color("color--background--primary--strong", ursaColors.blue600),
        active: color("color--background--primary--stronger", ursaColors.blue700),
        focus: color("color--background--primary--default", ursaColors.blue500),
        disabled: color("color--background--primary--weak", ursaColors.blue300),
    },
    "primary-empty": {
        rest: "transparent",
        hover: color("color--background--primary--weakest", ursaColors.blue50),
        active: color("color--background--primary--weaker", ursaColors.blue75),
        focus: color("color--background--neutral--weakest", ursaColors.neutral00),
        disabled: "transparent",
    },
    "secondary-filled": {
        rest: color("color--background--neutral--weaker", ursaColors.neutral50),
        hover: color("color--background--neutral--default", ursaColors.neutral75),
        active: color("color--background--neutral--strong", ursaColors.neutral100),
        focus: color("color--background--neutral--weaker", ursaColors.neutral50),
        disabled: color("color--background--neutral--weaker", ursaColors.neutral50),
    },
    "secondary-outlined": {
        rest: "transparent",
        hover: color("color--background--neutral--weak", ursaColors.neutral60),
        active: color("color--background--neutral--default", ursaColors.neutral75),
        focus: "transparent",
        disabled: color("color--background--neutral--weaker", ursaColors.neutral50),
    },
    "secondary-empty": {
        rest: "transparent",
        hover: color("color--background--neutral--weak", ursaColors.neutral60),
        active: color("color--background--neutral--default", ursaColors.neutral75),
        focus: "transparent",
        disabled: "transparent",
    },
    "canvas-filled": {
        rest: color("color--background--neutral--weakest", ursaColors.neutral35),
        hover: color("color--background--slate--weak", ursaColors.slate60),
        active: color("color--background--slate--default", ursaColors.slate75),
        focus: color("color--background--slate--weaker", ursaColors.slate50),
        disabled: color("color--background--neutral--weakest", ursaColors.neutral35),
    },
    "canvas-empty": {
        rest: color("color--background--surfaces--canvas", ursaColors.neutral25),
        hover: color("color--background--neutral--weaker", ursaColors.neutral50),
        active: color("color--background--neutral--default", ursaColors.neutral75),
        focus: "transparent",
        disabled: color("color--background--neutral--weakest", ursaColors.neutral35),
    },
    "destructive-filled": {
        rest: color("color--background--destructive--default", ursaColors.red500),
        hover: color("color--background--destructive--strong", ursaColors.red600),
        active: color("color--background--destructive--stronger", ursaColors.red700),
        focus: color("color--background--destructive--default", ursaColors.red500),
        disabled: color("color--background--destructive--weaker", ursaColors.red400),
    },
    "destructive-empty": {
        rest: "transparent",
        hover: color("color--background--destructive--weakest", ursaColors.red50),
        active: color("color--background--destructive--weaker", ursaColors.red75),
        focus: "transparent",
        disabled: "transparent",
    },
});

const canvasEmptyAndLodestarStates = {
    //appearance canvas-empty within the lodestar-light theme should map to secondary backgrounds with transparent rest
    rest: "transparent",
    focus: lodestarColors.buttonDefaultFocus, //#eeeeee no token exists
    hover: color("color--background--neutral--default", lodestarColors.buttonDefaultHover),
    active: color("color--background--neutral--strong", lodestarColors.buttonDefaultPressed),
    disabled: color("color--background--neutral--weaker", lodestarColors.buttonDefaultDisabled),
};

// For mapping state to the color object above
const mapStateToBackgroundString = ({ state, focusMode }: BaseButtonStyledProps) => {
    // Disabled should always win
    if (state.isDisabled) {
        return "disabled";
    }
    // Active should win over hover
    if (state.isActive) {
        return "active";
    }
    if (state.isFocus && focusMode === "keyboard") {
        return "focus";
    }
    if (state.isHover) {
        return "hover";
    }
    return "rest";
};

export const getButtonBackgroundColor = (props: BaseButtonStyledProps): string => {
    const resolvedState = mapStateToBackgroundString(props);
    const { themeMode, isCanvasEmptyAndLodestar } = props;
    if (themeMode === "lodestar-light" && isCanvasEmptyAndLodestar) {
        return canvasEmptyAndLodestarStates[resolvedState];
    }
    return buttonBackgroundColors[mapButtonAppearance(props.appearance, props.themeMode)][resolvedState](
        props.themeMode
    );
};

// For mapping state to the color object above
const mapStateToBackgroundVar = (state: ButtonRenderState, focusMode: BaseButtonStyledProps["focusMode"]) => {
    // Disabled should always win
    if (state.isDisabled) {
        return "--sds--button--color--background--disabled";
    }
    // Active should win over hover
    if (state.isActive) {
        return "--sds--button--color--background--active";
    }
    if (state.isFocus && focusMode === "keyboard") {
        return "--sds--button--color--background--focus";
    }
    if (state.isHover) {
        return "--sds--button--color--background--hover";
    }
    return "--sds--button--color--background--rest";
};

export const getButtonBackgroundColorVar = (
    state: ButtonRenderState,
    focusMode: BaseButtonStyledProps["focusMode"]
): string => {
    return `${mapStateToBackgroundVar(state, focusMode)}`;
};
